import { Selection } from '@leverege/ui-redux';
import { Attributes } from '@leverege/ui-attributes';
import { GeoshapeAdapter, GeoshapeStyle } from '@leverege/ui-mapbox-elements';
import Symbolizers from "../../shared/Symbolizers.js";
import Model from "./GeoPointModel.js";
import Util from "./Util.js";
const {
  getAccuracyFeature
} = Util;
const {
  SYMBOL_LAYOUT
} = GeoshapeStyle;
class GeoPointAdapter extends GeoshapeAdapter {
  constructor(opts) {
    super();
    this.Model = Model;
    this.attributeName = opts?.attributeName;
  }
  getShapeData(item, id, options) {
    const app = {
      visible: true,
      name: item.name
    };
    const llAttr = Attributes.getAttribute(this.attributeName, item.type);
    const ll = llAttr?.get(item.data);
    if (ll == null) {
      return null;
    }
    const geoJson = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Point',
        coordinates: [ll.lon, ll.lat]
      }
    };
    const {
      rollover,
      selected,
      model,
      symbols
    } = options;
    const subtype = Symbolizers.getSymbol(model.symbolizer, item, null);
    app.isRollover = Selection.isSelected(rollover, id);
    app.isSelected = Selection.isSelected(selected, id);
    app.layerGroup = app.isRollover ? 'rollover' : app.isSelected ? 'selected' : null;
    app.showSymbol = true;
    app.showLabel = false;
    app.symbol = Array.isArray(subtype) ? symbols.getTypeSymbol(subtype[0] || item.type, subtype[1], app.isRollover, app.isSelected) : symbols.getTypeSymbol(item.type, subtype, app.isRollover, app.isSelected);
    const features = [{
      dataType: item.type,
      dataRef: item.ref,
      geoJson,
      appearance: app
    }];
    const accuracyOpts = llAttr.source.attribute?.metadata?.accuracy;
    const showAccuracyOn = model && Model.getShowAccuracyOn(model);
    const showAccuracy = showAccuracyOn && (showAccuracyOn === 'always' || showAccuracyOn === 'selected' && Selection.isSelected(selected, item.id) || showAccuracyOn === 'rollover' && Selection.isSelected(rollover, item.id));
    if (accuracyOpts && showAccuracy) {
      const {
        attribute,
        multiplier
      } = accuracyOpts;
      const accuracyAttr = Attributes.getAttribute(attribute, item.type);
      const accuracy = accuracyAttr?.get(item.data);
      if (accuracy != null && (accuracyAttr.valueType === 'length' || accuracyAttr.valueType === 'number' || accuracyAttr.valueType === 'int')) {
        const accuracyFeature = getAccuracyFeature(ll, accuracy?.unit ? accuracy : {
          value: accuracy,
          unit: accuracyOpts.unit || 'meters'
        }, multiplier);
        features.push({
          geoJson: accuracyFeature,
          appearance: this.getAccuracyAppearance(item, options)
        });
      }
    }
    return features;
  }
  getAccuracyAppearance(item, {
    model
  }) {
    const app = {
      visible: true,
      name: `${item.id}.accuracy`,
      showSymbol: false,
      showLabel: false,
      showFill: true
    };
    const defaultColor = 'rgb(22, 97, 215)';
    app.fillColor = app.fillColor == null ? Util.getColor(Model.getFillColor(model), item) || defaultColor : app.fillColor;
    app.fillPattern = app.fillPattern == null ? Util.getPattern(Model.getFillPattern(model), item) : app.fillPattern;
    app.fillOpacity = app.fillOpacity == null ? Util.getNumber(Model.getFillOpacity(model), item) || 0.5 : app.fillOpacity;
    app.lineColor = app.lineColor == null ? Util.getColor(Model.getLineColor(model), item) || defaultColor : app.lineColor;
    app.lineOpacity = app.lineOpacity == null ? Util.getNumber(Model.getLineOpacity(model), item) || 1 : app.lineOpacity;
    app.linePattern = app.linePattern == null ? Util.getPattern(Model.getLinePattern(model), item) : app.linePattern;
    app.lineWidth = app.lineWidth == null ? Util.getNumber(Model.getLineWidth(model), item) : app.lineWidth;
    app.lineCap = app.lineCap == null ? Model.getLineCap(model) || 'round' : app.lineCap;
    app.lineJoin = app.lineJoin == null ? Model.getLineJoin(model) || 'round' : app.lineJoin;
    return app;
  }
  getFillPaint(style, layerGroup, options) {
    return {
      'fill-color': ['case', ['has', 'fillColor'], ['get', 'fillColor'], 'rgb(22, 97, 215)'],
      'fill-opacity': ['case', ['has', 'fillOpacity'], ['get', 'fillOpacity'], 0.5]
    };
  }
  getLinePaint(style, layerGroup, options) {
    return {
      'line-color': ['case', ['has', 'lineColor'], ['get', 'lineColor'], 'rgb(22, 97, 215)'],
      'line-width': ['case', ['has', 'lineWidth'], ['get', 'lineWidth'], 1],
      'line-offset': ['case', ['has', 'lineOffset'], ['get', 'lineOffset'], 0],
      'line-opacity': ['case', ['has', 'lineOpacity'], ['get', 'lineOpacity'], 1]
    };
  }
  getLineLayout(style) {
    return {
      'line-cap': 'round',
      'line-join': 'round'
    };
  }
  getLabelLayout() {
    return {
      'symbol-placement': 'point',
      'text-max-width': 4,
      // 'text-field' : '{name}',
      'text-field': ['format', ['get', 'name'], {
        'font-scale': 1.0
        // 'text-font' : [ 'Roboto Bold' ],
      }, '\n', {}, ['get', 'subtitle'], {
        'font-scale': 0.8,
        'text-color': 'rgb(0,0,0)'
        // 'text-font': ['literal', [ 'Roboto Regular', 'Arial Unicode MS Bold' ]]
      }],
      'text-font': ['Roboto Regular'],
      'text-anchor': 'center',
      'text-justify': 'center',
      'text-allow-overlap': false
    };
  }
  getLabelPaint(style, layerGroup, options) {
    const {
      model
    } = options;
    const show = model && this.Model.getShowAtZoom(model);
    const hide = model && this.Model.getHideAtZoom(model);
    const fade = model && this.Model.getFadeZoom(model) || 0;
    let opacity = 1;
    if (fade > 0.001) {
      if (typeof show === 'number') {
        if (typeof hide === 'number') {
          if (hide > show) {
            opacity = ['interpolate', ['exponential', 0.5], ['zoom'], show - fade, 0, show, 1, hide, 1, hide + fade, 0];
          }
        } else {
          opacity = ['interpolate', ['exponential', 0.5], ['zoom'], show - fade, 0, show, 1];
        }
      } else if (typeof hide === 'number') {
        opacity = ['interpolate', ['exponential', 0.5], ['zoom'], 1, hide, 1, hide + fade, 0];
      }
    } else if (typeof show === 'number') {
      if (typeof hide === 'number') {
        opacity = ['step', ['zoom'], 0, show, 1, hide, 0];
      } else {
        opacity = ['step', ['zoom'], 0, show, 1];
      }
    } else if (typeof hide === 'number') {
      opacity = ['step', ['zoom'], 1, hide, 0];
    }
    return {
      'text-color': ['case', ['has', 'color'], ['get', 'color'], 'rgb( 0,0,0 )'],
      'text-opacity': 0.8,
      'icon-opacity': opacity
      // Abrupt show/hide
      // "icon-opacity": [ 'step', ['zoom'], 0, 6, 1, 8, 0 ]
      // smoother show/hide
      // "icon-opacity": [ 'interpolate', ['exponential', 0.5 ], ['zoom'], 5.5, 0, 6, 1, 10, 1, 10.5, 0 ]
      // {

      //   stops: [ [6, 1], [8, 1] ],
      //   default : 0
      // }
    };
  }
  getSymbolLayout(style, layerGroup, options) {
    const {
      model
    } = options;
    const rScale = model && this.Model.getRolloverScale(model) || 1;
    const sScale = model && this.Model.getSelectedScale(model) || 1;
    const nScale = model && this.Model.getNormalScale(model) || 1;
    const scalesWithZoom = model && this.Model.getScalesWithZoom(model) || false;
    const scaleTo = model && this.Model.getScaleToZoom(model) || 10;
    const scaleFrom = model && this.Model.getScaleFromZoom(model) || scaleTo - 4;
    const toFac = model && this.Model.getScaleToFactor(model) || 1;
    const fromFac = model && this.Model.getScaleFromFactor(model) || 0;
    const flip = scaleTo < scaleFrom;
    let size = 0.5 * nScale;
    if (layerGroup === 'rollover') {
      size = 0.5 * rScale;
    } else if (layerGroup === 'selected') {
      size = 0.5 * sScale;
    }
    if (scalesWithZoom) {
      size = {
        stops: flip ? [[scaleTo, size * toFac], [scaleFrom, size * fromFac]] : [[scaleFrom, size * fromFac], [scaleTo, size * toFac]],
        default: size
      };
    }
    return {
      ...SYMBOL_LAYOUT,
      'icon-size': size
      // {
      //   // stops : [ [ 10, 0 ], [ 14, 0.25 ], [ 16, 0.75 ], [ 20, 0.5 ] ],
      //   stops : [ [ 7, 0 ], [ 12, 0.5 ] ],
      //   default : 1
      // }

      // 'icon-size' : {
      //   stops : [ [ -1, 0 ], [ 6, 0 ], [ 8, 1 ], [ 9, 0 ] ],
      //   default : 0
      // }
      // 'icon-opacity' :  {
      //   stops: [[6, 1.0], [8, 0], [16, 0.3]]
      // },
      // 'icon-opacity222': [
      //   'step',
      //   ['zoom'],
      //   0.5, -1,
      //   1, 6,
      //   1, 8,
      //   0.5
      //   ]
    };
    // return {
    //   ...SYMBOL_LAYOUT,
    //   'icon-size' : layerGroup === 'rollover' ? 1.0 : layerGroup === 'selected' ? 0.75 : 0.5,
    // }
  }

  /**
   * This returns the id of the layer which the style/layerGroup
   * layer should be placed before. The layer if must exist in the
   * map already: 'normal', 'selected', 'targeted', 'rollover' are
   * common examples
   * @param {string} style the style type of the layer group. This will be
   * the value returned in fillStyle, outlineStyle, labelStyle or 'default'.
   * @param {string} layerGroup the layerGroup type.
   * @param {object} options the options object
   */
  getBefore(style, layerGroup, options) {
    const {
      order
    } = options;
    switch (layerGroup) {
      case 'selected':
        return 'selected';
      case 'targeted':
        return 'targeted';
      case 'rollover':
        return 'rollover';
      default:
        {
          if (order) {
            return `normal-${order}`;
          }
          return 'normal';
        }
    }
    // return 'normal'
  }
}
export default GeoPointAdapter;